import React, { useState, useRef, useEffect } from "react";
import { Upload, RefreshCw } from "lucide-react";
import BaseButton from "../../Common/BaseButton";

const EmailImageUploader = ({
  initialImage,
  onImageUpload,
  allowRegenerate = false,
  onRegenerate = null,
  previewMode = false,
  label = "Image",
  height = "200px",
  width = "100%",
  maxSize = 5 * 1024 * 1024, // 5MB default
  placeholderText = "Upload an image",
  className = "",
}) => {
  const [image, setImage] = useState(initialImage);
  const [imageUrl, setImageUrl] = useState("");
  const [imageError, setImageError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isUploading && initialImage !== image) {
      setImage(initialImage);
      setImageError(false);
    }

    if (image instanceof File) {
      const url = URL.createObjectURL(image);
      setImageUrl(url);

      return () => URL.revokeObjectURL(url); // Clean up
    }

    if (typeof image === "string") {
      setImageUrl(image);
    }

    if (!image) {
      setImageUrl(null);
    }
  }, [initialImage, isUploading, image]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsUploading(true);

      // Validate file size
      if (file.size > maxSize) {
        setImageError(
          `File too large. Maximum size is ${maxSize / 1024 / 1024}MB.`
        );
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      // Notify parent component with the isImageUpload flag
      if (onImageUpload) {
        console.log("Calling parent onImageUpload with new image data");
        try {
          onImageUpload(file);
          setImage(file);
        } catch (err) {
          console.error("Error in parent onImageUpload callback:", err);
        }
      }

      // const reader = new FileReader();
      // reader.onloadend = () => {
      //   const imageData = reader.result;
      //   setImage(imageData);
      //   setImageError(false);

      //   if (onImageUpload) {
      //     onImageUpload(imageData);
      //   }

      //   setTimeout(() => setIsUploading(false), 300);
      //   if (fileInputRef.current) fileInputRef.current.value = "";
      // };

      // reader.onerror = () => {
      //   setImageError("Error reading image file");
      //   setIsUploading(false);
      //   if (fileInputRef.current) fileInputRef.current.value = "";
      // };

      // reader.readAsDataURL(file);
    }
  };

  const handleRegenerate = () => {
    if (onRegenerate) {
      onRegenerate();
    }
  };

  return (
    <div className={`email-image-upload-container ${className}`}>
      {!previewMode && (
        <>
          {label && (
            <label className="form-label">
              <strong>{label}</strong>
            </label>
          )}
          <div className="d-flex gap-2 mb-2">
            {allowRegenerate && (
              <BaseButton
                className="outlineprimary"
                children="Regenerate Image"
                imageSrc="/regenerate-image.svg"
                handleClick={handleRegenerate}
              />
            )}
            <label className="btn btn-outline-secondary btn-sm mb-0">
              <Upload size={16} className="me-1" />
              Upload Image
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </label>
          </div>
        </>
      )}

      <div
        className="image-preview-container rounded p-2 d-flex justify-content-center align-items-center"
        style={{
          minHeight: height,
          width: width,
          backgroundColor: "#f8f9fa",
          border: imageError ? "1px solid #dc3545" : "1px solid #dee2e6",
        }}
      >
        {image ? (
          <img
            src={imageUrl || "/left-image-placeholder.png"}
            alt="Preview"
            className="img-fluid rounded"
          />
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100 outline-none rounded">
            <div className="text-center">
              <img
                src="/img-preview.svg"
                alt="Preview"
                className="img-fluid mb-2"
              />
              <p className="text-muted small">{placeholderText}</p>
            </div>
          </div>
        )}
      </div>

      {imageError && <div className="text-danger mt-1 small">{imageError}</div>}
    </div>
  );
};

export default EmailImageUploader;
